/** Main-thread handle on the DB worker. One per tab, created on first use. */
import type { SyncStatus } from "@homing/core/client";
import { createStore, type Store } from "@homing/core/client";
import { trustedScriptUrl } from "../lib/trusted.ts";
import type { Call, DbApi, FromWorker, Method } from "./api.ts";
// oxlint-disable-next-line import/default -- a Vite query import (typed by vite/client), not a module oxlint can resolve
import workerUrl from "./worker.ts?worker&url";

export interface DbState {
  /** account whose DB is open and ready to query */
  account: string | null;
  sync: SyncStatus | null;
  blocked: boolean;
  error: string | null;
}

type Api = { [K in Method]: (...args: Parameters<DbApi[K]>) => ReturnType<DbApi[K]> };

export class MailDb {
  private readonly worker: Worker;
  private seq = 0;
  private readonly calls = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
  private readonly changeSubs = new Set<(tables: ReadonlySet<string>) => void>();
  private readonly rejectSubs = new Set<(error: string) => void>();
  private readonly state$ = createStore<DbState>({ account: null, sync: null, blocked: false, error: null });
  readonly state: Store<DbState> = this.state$;
  private wanted: string | null = null;

  constructor() {
    this.worker = new Worker(trustedScriptUrl(workerUrl), { type: "module", name: "homing-db" });
    this.worker.addEventListener("message", (e: MessageEvent<FromWorker>) => this.receive(e.data));
    this.worker.addEventListener("error", (e) => {
      this.state$.update({ error: e.message || "The local database failed to start." });
      for (const c of this.calls.values()) c.reject(new Error("worker error"));
      this.calls.clear();
    });
    const online = () => void this.api.setOnline(navigator.onLine);
    addEventListener("online", online);
    addEventListener("offline", online);
    online();
  }

  private receive(m: FromWorker) {
    if ("reply" in m) {
      const c = this.calls.get(m.reply.id);
      if (!c) return;
      this.calls.delete(m.reply.id);
      if (m.reply.ok) c.resolve(m.reply.value);
      else c.reject(new Error(m.reply.error));
      return;
    }
    const ev = m.event;
    if (ev.type === "blocked") this.state$.update({ blocked: true });
    else if (ev.type === "fatal") this.state$.update({ error: ev.error });
    else if (ev.account !== this.wanted) return;
    else if (ev.type === "status") this.state$.update({ sync: ev.status, blocked: false });
    else if (ev.type === "changed") for (const cb of this.changeSubs) cb(new Set(ev.tables));
    else if (ev.type === "rejected") for (const cb of this.rejectSubs) cb(ev.error);
  }

  private call<K extends Method>(method: K, ...args: Parameters<DbApi[K]>): ReturnType<DbApi[K]> {
    const id = ++this.seq;
    const p = new Promise((resolve, reject) => {
      this.calls.set(id, { resolve, reject });
      // oxlint-disable-next-line unicorn/require-post-message-target-origin -- Worker.postMessage has no targetOrigin
      this.worker.postMessage({ id, method, args } satisfies Call);
    });
    return p as ReturnType<DbApi[K]>;
  }

  readonly api: Api = {
    open: (a) => this.call("open", a),
    query: (sql, params) => this.call("query", sql, ...(params ? [params] : [])),
    mutate: (name, args) => this.call("mutate", name, args),
    body: (id) => this.call("body", id),
    syncNow: () => this.call("syncNow"),
    setOnline: (o) => this.call("setOnline", o),
    wipe: () => this.call("wipe"),
  };

  async open(accountId: string): Promise<void> {
    if (this.wanted === accountId) return;
    this.wanted = accountId;
    this.state$.set({ account: null, sync: null, blocked: false, error: null });
    try {
      await this.api.open(accountId);
      if (this.wanted === accountId) this.state$.update({ account: accountId });
    } catch (e) {
      if (this.wanted === accountId) this.state$.update({ error: e instanceof Error ? e.message : String(e) });
    }
  }

  onChange(cb: (tables: ReadonlySet<string>) => void): () => void {
    this.changeSubs.add(cb);
    return () => void this.changeSubs.delete(cb);
  }

  onRejected(cb: (error: string) => void): () => void {
    this.rejectSubs.add(cb);
    return () => void this.rejectSubs.delete(cb);
  }

  async wipe(): Promise<void> {
    this.wanted = null;
    await this.api.wipe();
  }
}

let instance: MailDb | null = null;
export const mailDb = () => (instance ??= new MailDb());
export const existingMailDb = () => instance;
